import { useState, useEffect } from 'react'

const DARK_CLASS  = 'dark'
const STORAGE_KEY = 'aj-theme'

/**
 * Call once before React renders (in main.jsx) to apply the saved or
 * preferred theme without a flash of unstyled content.
 */
export function initTheme() {
  const saved = localStorage.getItem(STORAGE_KEY)
  // Default is always light — only go dark if the user has explicitly chosen it
  const dark  = saved === 'dark'
  document.body.classList.toggle(DARK_CLASS, dark)
}

/** Current dark-mode state — safe to call outside React. */
export function isDark() {
  return document.body.classList.contains(DARK_CLASS)
}

/**
 * Toggle dark/light, persist to localStorage, fire `theme-change` so
 * React components that use useDarkMode() re-render automatically.
 */
export function toggleTheme() {
  const dark = !isDark()
  document.body.classList.toggle(DARK_CLASS, dark)
  localStorage.setItem(STORAGE_KEY, dark ? 'dark' : 'light')
  window.dispatchEvent(new CustomEvent('theme-change', { detail: { dark } }))
}

/**
 * React hook — returns true when dark mode is active and re-renders on
 * every theme change.  Import this in any component that has inline
 * styles that need to swap between light and dark values.
 *
 * Usage:
 *   const dark = useDarkMode()
 *   <div style={{ background: dark ? '#142c34' : '#ffffff' }}>
 */
export function useDarkMode() {
  const [dark, setDark] = useState(() => isDark())
  useEffect(() => {
    function onTheme(e) { setDark(e.detail.dark) }
    window.addEventListener('theme-change', onTheme)
    return () => window.removeEventListener('theme-change', onTheme)
  }, [])
  return dark
}
